import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Stack,
  Avatar,
  Grid,
  Chip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const EMPTY_VALUE = '-';

const formatearFechaSimple = (fecha) => {
  if (!fecha) return EMPTY_VALUE;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return EMPTY_VALUE;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const DataItem = ({ icon: Icon, label, value, color = '#1e293b' }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
    <Icon sx={{ fontSize: 16, color: '#64748b' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.82rem', color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value || EMPTY_VALUE}
      </Typography>
    </Box>
  </Stack>
);

const ESTADOS_MENSAJES = {
  Default: 'Pendiente',
  Proceso: 'En Proceso',
  Autorizada: 'Autorizada',
  Pagada: 'Pagada',
  Cancelada: 'Cancelada',
  Vencida: 'Vencida',
  'Cancelada ERP': 'Cancelada ERP',
  'Sin solicitud': 'Sin solicitud',
};

const COLORES_ESTADO = {
  Default: '#64748b',
  Proceso: '#ed6c02',
  Autorizada: '#0288d1',
  Pagada: '#2e7d32',
  Cancelada: '#d32f2f',
  Vencida: '#9a3412',
  'Cancelada ERP': '#475569',
  'Sin solicitud': '#64748b',
};

const SolicitudProgreso = ({
  open,
  onClose,
  cadenaAut,
  estadoOrden,
  detalle,
  rowRaw,
  solicitudesRelacionadas,
  loadingSolicitudesRelacionadas = false,
}) => {
  const statusColor = COLORES_ESTADO[estadoOrden] || COLORES_ESTADO.Default;
  const tieneSolicitud = Boolean(rowRaw?.ID_SOLICITUD || (cadenaAut || []).length || (estadoOrden && estadoOrden !== 'Sin solicitud'));
  const tituloModal = tieneSolicitud ? 'Seguimiento de Orden' : 'Detalle de Orden';
  const relacionadas = Array.isArray(solicitudesRelacionadas?.solicitudes) ? solicitudesRelacionadas.solicitudes : [];
  const relacionadasFuera = relacionadas.filter((item) => !item.enRango);
  const relacionadasDentro = relacionadas.filter((item) => item.enRango);
  const tieneVarias = relacionadas.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      sx={{ '& .MuiDialog-paper': { borderRadius: 2, m: 2 } }}
    >
      <DialogTitle
        sx={{
          p: 2,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar sx={{ bgcolor: '#0655A5', width: 32, height: 32 }}>
            <ReceiptLongIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b', lineHeight: 1.1 }}>
              {tituloModal}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
              Folio: {detalle?.folio || EMPTY_VALUE}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1.5,
              bgcolor: `${statusColor}15`,
              color: statusColor,
              fontSize: '0.75rem',
              fontWeight: 800,
              ml: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {ESTADOS_MENSAJES[estadoOrden] || estadoOrden}
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, bgcolor: '#ffffff' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <DataItem icon={PersonIcon} label="Comprador" value={detalle?.solicitante} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <DataItem icon={BusinessIcon} label="Centro Costo" value={detalle?.centroCosto} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <DataItem icon={PaymentsIcon} label="Monto" value={detalle?.monto} color="#2e7d32" />
            </Grid>
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <DataItem icon={CalendarMonthIcon} label="Fecha OC" value={detalle?.fechaSoli ? formatearFechaSimple(detalle.fechaSoli) : EMPTY_VALUE} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <DataItem icon={EventAvailableIcon} label="Vencimiento" value={detalle?.vencimiento ? formatearFechaSimple(detalle.vencimiento) : EMPTY_VALUE} />
            </Grid>
          </Grid>
        </Box>

        {(loadingSolicitudesRelacionadas || relacionadas.length > 0) && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.9,
                border: '1px solid #dbe4ef',
                borderRadius: 1.5,
                bgcolor: tieneVarias ? '#eff6ff' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.76rem', color: '#334155', fontWeight: 900 }}>
                  Historial relacionado
                </Typography>
                <Typography sx={{ mt: 0.1, fontSize: '0.7rem', color: '#64748b', fontWeight: 700 }}>
                  {loadingSolicitudesRelacionadas
                    ? 'Consultando solicitudes de esta OC...'
                    : `${relacionadas.length} ${relacionadas.length === 1 ? 'solicitud' : 'solicitudes'} - ${relacionadasDentro.length} en rango, ${relacionadasFuera.length} fuera del rango`}
                </Typography>
              </Box>
              {loadingSolicitudesRelacionadas ? (
                <CircularProgress size={16} />
              ) : (
                <Chip
                  size="small"
                  label={tieneVarias ? `${relacionadas.length} solicitudes` : '1 solicitud'}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 900,
                    bgcolor: tieneVarias ? '#005eb8' : '#e0f2fe',
                    color: tieneVarias ? '#ffffff' : '#075985',
                  }}
                />
              )}
            </Box>

            {!loadingSolicitudesRelacionadas && (
              <Stack spacing={0.75} sx={{ mt: 0.8, maxHeight: 230, overflow: 'auto', pr: 0.5 }}>
                {relacionadas.map((item) => {
                  const color = COLORES_ESTADO[item.estatus] || COLORES_ESTADO.Default;
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        p: 0.9,
                        border: '1px solid #e2e8f0',
                        borderRadius: 1.25,
                        bgcolor: item.enRango ? '#ffffff' : '#f8fafc',
                        opacity: item.enRango ? 1 : 0.58,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                        gap: 0.8,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 900 }}>
                          Solicitud #{item.id} - {item.fechaSolicitud || 'Sin fecha'}
                        </Typography>
                        <Typography sx={{ mt: 0.15, fontSize: '0.68rem', color: '#64748b', fontWeight: 700 }}>
                          {item.solicitante || 'Sin solicitante'} - {item.monto || '$0.00'} {item.divisa || ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <Chip size="small" label={item.enRango ? 'En rango' : 'Fuera de rango'} sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800 }} />
                        <Chip size="small" label={item.estatus || 'Default'} sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: `${color}18`, color }} />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {Array.isArray(cadenaAut) && cadenaAut.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 800, mb: 1, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Flujo de Autorizaciones
            </Typography>
            <Stack spacing={0.8}>
              {cadenaAut.map((item, index) => {
                const isApproved = item.ESTADO === 'CHECK_GREEN';
                const isCanceled = item.ESTADO === 'CANCEL_RED';
                const isTesorero = String(item.ROL || '').toLowerCase().includes('tesorero');

                const statusColor = isApproved ? '#166534' : isCanceled ? '#991b1b' : '#854d0e';
                const statusBg = isApproved ? '#f0fdf4' : isCanceled ? '#fef2f2' : '#fffbe6';
                const statusBorder = isApproved ? '#dcfce7' : isCanceled ? '#fee2e2' : '#fef3c7';

                return (
                  <Box
                    key={index}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderRadius: 1.5,
                      bgcolor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: isTesorero ? '#6b21a8' : '#0288d1' }}>
                          {item.ROL || `Nivel ${index + 1}`}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                          {item.USUARIO || 'No asignado'}
                        </Typography>
                      </Stack>
                      {item.FECHA_AUTORIZACION && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, mt: 0.1 }}>
                          Autorizado el {formatearFechaSimple(item.FECHA_AUTORIZACION)}
                        </Typography>
                      )}
                    </Box>

                    <Chip
                      size="small"
                      label={isApproved ? 'Autorizado' : isCanceled ? 'Cancelado' : 'Pendiente'}
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        bgcolor: statusBg,
                        color: statusColor,
                        border: `1px solid ${statusBorder}`,
                        height: 22,
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {estadoOrden === 'Pagada' && (
          <Box sx={{ mx: 2, mb: 2, p: 1.25, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#166534', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#166534', lineHeight: 1.1 }}>
                Orden Pagada
              </Typography>
              {(rowRaw?.FECHA_PAGO || rowRaw?.FECHAPAGO || detalle?.fechaPago) && (
                <Typography sx={{ fontSize: '0.72rem', color: '#166534', fontWeight: 600, mt: 0.2 }}>
                  El pago fue realizado el día {formatearFechaSimple(rowRaw?.FECHA_PAGO || rowRaw?.FECHAPAGO || detalle?.fechaPago)}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {estadoOrden === 'Cancelada ERP' && (
          <Box sx={{ mx: 2, mb: 2, p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsSuggestIcon sx={{ color: '#475569' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>
              Esta solicitud fue cancelada directamente desde el ERP.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
        <Button fullWidth onClick={onClose} variant="contained" sx={{ bgcolor: '#0655A5', '&:hover': { bgcolor: '#044485' }, textTransform: 'none', fontWeight: 700, borderRadius: 1.5, py: 1 }}>
          Cerrar Seguimiento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolicitudProgreso;
